import markup from "./native-effects-controls.html?raw";
import { NativeShadowControls } from "./native-shadow-controls";
import { enableShadow } from "../native/shadow";
import {
  changeEffect,
  effects,
  edgeStyles,
  type NativeEffects,
} from "../native/effects";
import type { SceneNode, EngineDocument } from "../scene/model";
import type { NativeSession } from "../native/session";
import { originalIcon } from "./native-original-icons";
import {
  originalCompact,
  syncOriginalCompact,
} from "./native-original-controls";
import { applyImageEdgesToCanvas } from "../render/native-pixel-effects.js";
const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
type Transaction = {
  kind: "background" | "edge";
  original: SceneNode;
  preview: SceneNode;
  dirty: boolean;
  generation: number;
};
/** Ordinary controls commit once per gesture. Background/edge previews never enter the live document. */
export class NativeEffectsEditor {
  private transaction: Transaction | null = null;
  private slider = false;
  private selection = "";
  private portal: {
    section: HTMLDetailsElement;
    placeholder: Comment;
    popup: HTMLElement;
  } | null = null;
  private row: HTMLElement;
  private shape: HTMLDetailsElement;
  private shadowControls: NativeShadowControls;
  picking = false;
  private exit = document.createElement("dialog");
  constructor(
    private session: NativeSession,
    private changed: () => void,
    private prepare: () => boolean,
    private sample: (x: number, y: number) => string | null,
  ) {
    el("native-right-panel")
      .querySelector(".native-panel-scroll")
      ?.insertAdjacentHTML("beforeend", markup);
    if (!el("image-properties"))
      el("native-right-panel").insertAdjacentHTML("beforeend", markup);
    const panel = el("image-properties"),
      controls = panel.querySelector(".image-property-controls")!;
    panel.querySelector("h3")!.remove();
    for (const id of ["image-opacity", "image-corner-radius"]) {
      const input = el(id);
      input.previousElementSibling?.remove();
      input.remove();
    }
    const correction = this.group("image-correction-group", "Коррекция", "hue");
    for (const id of ["image-hue", "image-saturation"]) {
      const input = el(id),
        wrap = input.parentElement!;
      correction.lastElementChild!.append(wrap.previousElementSibling!, wrap);
    }
    correction.lastElementChild!.append(el("image-blend-mode").parentElement!);
    const fx = this.group("image-effects-group", "Эффекты", "blur");
    el("gauss-blur-controls").style.display = "";
    fx.lastElementChild!.append(el("gauss-blur-controls"));
    const bg = el<HTMLDetailsElement>("image-background-removal");
    bg.classList.add("native-properties");
    bg.querySelector("summary")!.insertAdjacentHTML(
      "afterbegin",
      originalIcon("cutout"),
    );
    bg.querySelector(".background-removal-toggle")!.remove();
    el("background-removal-settings").hidden = false;
    const edge = this.group("image-edges-group", "Края", "cutout");
    this.edgeControls(edge.lastElementChild as HTMLElement);
    panel.append(correction, fx, edge, bg);
    controls.remove();
    bg.lastElementChild!.append(this.actions("background"));
    for (const [id, key] of [
      ["image-hue", "hue"],
      ["image-saturation", "saturation"],
      ["gauss-blur", "blur"],
    ] as const)
      this.range(id, key);
    el<HTMLSelectElement>("image-blend-mode").onchange = () =>
      this.simple((nodes) =>
        nodes
          .filter((n) => n.kind === "image")
          .map((n) => ({
            ...n,
            appearance: {
              ...n.appearance,
              blendMode: el<HTMLSelectElement>("image-blend-mode").value,
            },
          })),
      );
    this.shape = this.group("native-shape-effects", "Эффекты", "shadow");
    this.shadowControls = new NativeShadowControls(
      session,
      changed,
      () => !this.transaction && !this.slider && this.prepare(),
    );
    this.shape.lastElementChild!.append(this.shadowControls.element);
    document
      .querySelector('[data-property-section="appearance"]')!
      .after(this.shape);
    el<HTMLInputElement>("show-shadow").onchange = () =>
      this.simple((nodes) =>
        enableShadow(nodes, el<HTMLInputElement>("show-shadow").checked),
      );
    for (const [details, kind] of [
      [bg, "background"],
      [edge, "edge"],
    ] as const)
      details.addEventListener("toggle", () => {
        if (details.open && this.transaction?.kind !== kind) this.begin(kind);
        else if (!details.open && this.transaction?.kind === kind) {
          if (kind === "background" && this.transaction.dirty) {
            details.open = true;
            this.prompt();
          } else this.finish(false);
        }
      });
    for (const id of [
      "background-removal-sensitivity",
      "background-removal-softness",
      "background-removal-color",
      "background-removal-enclosed",
    ]) {
      el(id).addEventListener("input", () =>
        this.background(false, id === "background-removal-color"),
      );
    }
    el("background-removal-auto").onclick = () => this.background(true);
    el("background-removal-reset").onclick = () => {
      if (this.transaction) {
        this.preview("background", {
          enabled: false,
          color: "",
          sensitivity: 28,
          softness: 12,
          mode: "all",
        });
      }
    };
    el("background-removal-pick").onclick = () => {
      this.picking = !this.picking;
      this.changed();
    };
    this.exit.innerHTML =
      '<h2>Настройки удаления фона изменены</h2><p>Применить изменения перед выходом?</p><button data-choice="apply">Применить</button><button data-choice="discard">Не применять</button><button data-choice="stay">Продолжить редактирование</button>';
    el("native-editor").append(this.exit);
    this.exit.querySelectorAll<HTMLButtonElement>("button").forEach(
      (b) =>
        (b.onclick = () => {
          this.exit.close();
          if (b.dataset.choice !== "stay")
            this.finish(b.dataset.choice === "apply");
        }),
    );
    document.addEventListener("pointerdown", (e) => this.outside(e), true);
    document.addEventListener(
      "click",
      (e) => {
        if (!this.transaction) this.outside(e);
      },
      true,
    );
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Escape") return;
        if (this.exit.open) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.exit.close();
          return;
        }
        if (this.transaction) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.finish(false);
        } else if (this.slider) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.session.cancel();
          this.slider = false;
          this.changed();
        } else if (
          this.portal &&
          !document.querySelector("#irl-color-picker:not([hidden])")
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.closePortal();
        }
      },
      true,
    );
    this.row = originalCompact("native-effects-compact", [
      { id: "gauss-blur", icon: "blur", title: "Размытие по Гауссу" },
      { id: "show-shadow", icon: "shadow", title: "Тень" },
    ]);
    // The dock uses a third row of tools, never the inspector's full panel.
    const shadowTrigger = this.row.querySelector<HTMLButtonElement>(
      '[data-original-control="show-shadow"]',
    )!;
    shadowTrigger.onclick = () => {
      this.shadowControls.finish(true);
      if (this.active) return;
      this.closePortal();
      const checkbox = el<HTMLInputElement>("show-shadow");
      if (this.shadowControls.compact.opened && checkbox.checked)
        checkbox.click();
      else {
        if (!checkbox.checked || checkbox.indeterminate) checkbox.click();
        if (checkbox.checked) this.shadowControls.compact.open();
      }
      shadowTrigger.setAttribute(
        "aria-expanded",
        String(this.shadowControls.compact.opened),
      );
    };
    shadowTrigger
      .parentElement!.querySelector(".native-property-popup")
      ?.remove();
    for (const [id, title, icon] of [
      ["image-correction-group", "Коррекция", "hue"],
      ["image-edges-group", "Края", "cutout"],
      ["image-background-removal", "Удаление фона", "cutout"],
    ]) {
      const button = document.createElement("button");
      button.title = button.ariaLabel = title!;
      button.innerHTML = originalIcon(icon!);
      button.dataset.effectSection = id;
      button.onpointerdown = (e) => e.preventDefault();
      button.onclick = () => this.openPortal(el<HTMLDetailsElement>(id!));
      this.row.append(button);
    }
  }
  get active() {
    return !!this.transaction || this.slider || this.shadowControls.active;
  }
  get previewDocument(): EngineDocument {
    const t = this.transaction;
    if (!t) return this.session.document;
    return {
      ...this.session.document,
      pages: [
        {
          ...this.session.page,
          nodes: this.session.page.nodes.map((n) =>
            n.id === t.original.id ? t.preview : n,
          ),
        },
      ],
    };
  }
  private group(id: string, title: string, icon: string) {
    const d = document.createElement("details");
    d.id = id;
    d.className = "native-properties";
    d.innerHTML = `<summary>${originalIcon(icon)}<span>${title}</span></summary><div class="native-property-body"></div>`;
    return d;
  }
  private simple(change: (nodes: SceneNode[]) => SceneNode[]) {
    if (this.active || !this.prepare()) return;
    this.session.replaceMany(change(this.session.selectedNodes));
    this.session.finish();
    this.changed();
  }
  private range(id: string, key: "hue" | "saturation" | "blur") {
    const input = el<HTMLInputElement>(id),
      output = el<HTMLOutputElement>(id + "-value");
    input.oninput = () => {
      if (!this.slider) {
        if (this.transaction || !this.prepare()) return;
        this.session.begin();
        this.slider = true;
      }
      let value = Number(input.value);
      const snap = Number(input.dataset.zeroSnap || 0);
      if (Math.abs(value) < snap) value = 0;
      this.session.replaceMany(
        changeEffect(
          this.session.selectedNodes.filter((n) => n.kind === "image"),
          key,
          value,
        ),
      );
      this.changed();
    };
    input.onchange = () => {
      if (this.slider) {
        this.session.finish();
        this.slider = false;
        this.changed();
      }
    };
    const commit = () => {
      const value = parseFloat(output.textContent || "");
      if (Number.isFinite(value)) {
        input.value = String(value);
        input.dispatchEvent(new Event("input"));
        input.dispatchEvent(new Event("change"));
      }
      this.changed();
    };
    output.onblur = commit;
    output.onkeydown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        output.blur();
      }
    };
  }
  private begin(kind: Transaction["kind"]) {
    if (this.transaction) this.finish(false);
    if (!this.prepare() || this.session.selected?.kind !== "image") {
      el<HTMLDetailsElement>(
        kind === "edge" ? "image-edges-group" : "image-background-removal",
      ).open = false;
      return;
    }
    const n = this.session.selected;
    this.transaction = {
      kind,
      original: n,
      preview: n,
      dirty: false,
      generation: this.session.generation,
    };
    if (kind === "background")
      this.preview(
        "background",
        { ...effects(n).background, enabled: true },
        false,
      );
    this.changed();
  }
  private preview<K extends keyof NativeEffects>(
    key: K,
    value: NativeEffects[K],
    dirty = true,
  ) {
    const t = this.transaction;
    if (!t) return;
    t.preview = changeEffect([t.preview], key, value)[0]!;
    t.dirty ||= dirty;
    this.changed();
  }
  private background(auto = false, manual = false) {
    const t = this.transaction;
    if (!t) return;
    this.preview("background", {
      enabled: true,
      color: auto
        ? ""
        : manual
          ? el<HTMLInputElement>("background-removal-color").value
          : effects(t.preview).background.color,
      sensitivity: Number(
        el<HTMLInputElement>("background-removal-sensitivity").value,
      ),
      softness: Number(
        el<HTMLInputElement>("background-removal-softness").value,
      ),
      mode: el<HTMLInputElement>("background-removal-enclosed").checked
        ? "all"
        : "edge",
    });
  }
  finish(apply: boolean) {
    this.shadowControls.finish(apply);
    const t = this.transaction;
    this.transaction = null;
    this.picking = false;
    this.exit.close();
    if (
      t &&
      apply &&
      t.generation === this.session.generation &&
      this.session.page.nodes.find((n) => n.id === t.original.id) === t.original
    ) {
      this.session.replaceNode(t.preview);
      this.session.finish();
    }
    for (const id of ["image-edges-group", "image-background-removal"])
      el<HTMLDetailsElement>(id).open = false;
    this.closePortal();
    this.changed();
  }
  private prompt() {
    if (!this.exit.open) this.exit.showModal();
  }
  private outside(e: MouseEvent) {
    const target = e.target as HTMLElement,
      t = this.transaction;
    if (this.picking && target.id === "native-overlay") {
      const color = this.sample(e.clientX, e.clientY);
      if (color && t) {
        this.preview("background", { ...effects(t.preview).background, color });
        this.picking = false;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!t) {
      if (
        this.portal &&
        !this.portal.popup.contains(target) &&
        !target.closest(`[data-effect-section="${this.portal.section.id}"]`) &&
        !target.closest("#irl-color-picker")
      )
        this.closePortal();
      return;
    }
    if (
      this.exit.open ||
      target.closest(
        "#image-edges-group,#image-background-removal,#native-effects-compact,.irl-color-picker",
      ) ||
      target === this.exit
    )
      return;
    if (t.kind === "background" && t.dirty) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.prompt();
    } else this.finish(false);
  }
  private actions(kind: Transaction["kind"]) {
    const row = document.createElement("div");
    row.className = "property-edit-actions";
    for (const [action, icon, title] of [
      ["cancel", "cancel", "Отменить"],
      ["apply", "confirm", "Применить"],
    ]) {
      const b = document.createElement("button");
      b.type = "button";
      b.dataset.effectAction = action;
      b.id = kind + "-removal-" + action;
      b.title = b.ariaLabel = title!;
      b.innerHTML = originalIcon(icon!);
      if (action === "apply") b.className = "is-primary";
      b.onclick = () => this.finish(action === "apply");
      row.append(b);
    }
    return row;
  }
  private edgeControls(body: HTMLElement) {
    body.classList.add("image-edge-editor");
    const grid = document.createElement("div");
    grid.className = "image-edge-presets";
    for (const [style, label] of edgeStyles) {
      const b = document.createElement("button");
      b.className = "image-edge-preset";
      b.dataset.edgeStyle = style;
      b.title = b.ariaLabel = label;
      const canvas = document.createElement("canvas");
      canvas.width = 112;
      canvas.height = 56;
      const ctx = canvas.getContext("2d")!,
        g = ctx.createLinearGradient(0, 0, 112, 56);
      g.addColorStop(0, "#659bdd");
      g.addColorStop(0.55, "#85c7cb");
      g.addColorStop(1, "#e8bc82");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 112, 56);
      applyImageEdgesToCanvas(canvas, { style, amount: 70 });
      b.append(
        canvas,
        Object.assign(document.createElement("span"), { textContent: label }),
      );
      b.onclick = () => {
        if (this.transaction)
          this.preview("edge", {
            style,
            amount: effects(this.transaction.preview).edge.amount,
          });
      };
      grid.append(b);
    }
    const label = document.createElement("label");
    label.className = "image-edge-amount";
    label.innerHTML =
      '<span>Глубина края <output></output></span><input type="range" min="1" max="150" step="1" aria-label="Глубина края">';
    label.querySelector("input")!.oninput = (e) => {
      if (this.transaction)
        this.preview("edge", {
          ...effects(this.transaction.preview).edge,
          amount: Number((e.target as HTMLInputElement).value),
        });
    };
    body.append(grid, label, this.actions("edge"));
  }
  private openPortal(section: HTMLDetailsElement) {
    const same = this.portal?.section === section;
    this.closePortal();
    if (same) return;
    const placeholder = document.createComment("original properties position");
    section.before(placeholder);
    const popup = document.createElement("div");
    popup.className = "native-property-popup native-effects-popup";
    this.row.append(popup);
    popup.append(section);
    this.portal = { section, placeholder, popup };
    section.open = true;
  }
  private closePortal() {
    const p = this.portal;
    if (!p) return;
    this.portal = null;
    p.placeholder.replaceWith(p.section);
    p.popup.remove();
  }
  render() {
    const selected = this.session.selectedNodes,
      n = this.session.selected,
      key = this.session.generation + ":" + selected.map((n) => n.id).join();
    if (
      this.transaction &&
      (this.transaction.generation !== this.session.generation ||
        n?.id !== this.transaction.original.id)
    )
      this.finish(false);
    if (this.selection !== key) {
      this.closePortal();
      this.shadowControls.compact.close();
      this.selection = key;
    }
    el("image-properties").hidden = n?.kind !== "image";
    this.shape.hidden = !selected.length || n?.kind === "image";
    const shadowRow = this.shadowControls.element;
    const shadowParent =
      n?.kind === "image"
        ? el("image-effects-group").lastElementChild!
        : this.shape.lastElementChild!;
    if (shadowRow.parentElement !== shadowParent)
      shadowParent.append(shadowRow);
    this.row.hidden = !selected.length;
    const radiusAnchor = document.querySelector(
      '#native-compact-properties [data-native-popover="radius"]',
    )?.parentElement;
    if (radiusAnchor && radiusAnchor.nextElementSibling !== this.row)
      radiusAnchor.after(this.row);
    this.row
      .querySelectorAll<HTMLElement>("[data-effect-section]")
      .forEach((b) => (b.hidden = n?.kind !== "image"));
    this.row.querySelector<HTMLElement>(
      '[data-original-control="gauss-blur"]',
    )!.parentElement!.hidden = n?.kind !== "image";
    this.row.querySelector<HTMLElement>(
      '[data-original-control="show-shadow"]',
    )!.parentElement!.hidden = !selected.length;
    if (!n && !selected.length) return;
    const value = effects(this.transaction?.preview ?? n ?? selected[0]!);
    for (const [id, key] of [
      ["image-hue", "hue"],
      ["image-saturation", "saturation"],
      ["gauss-blur", "blur"],
    ] as const) {
      const input = el<HTMLInputElement>(id);
      if (document.activeElement !== input) input.value = String(value[key]);
      const output = el(id + "-value");
      if (document.activeElement !== output)
        output.textContent = value[key] + (key === "hue" ? "°" : "%");
    }
    el<HTMLSelectElement>("image-blend-mode").value =
      n?.appearance.blendMode ?? "source-over";
    const shadow = el<HTMLInputElement>("show-shadow");
    shadow.checked = selected.every((n) => effects(n).shadow);
    shadow.indeterminate =
      selected.some((n) => effects(n).shadow) && !shadow.checked;
    this.shadowControls.render();
    const shadowTrigger = this.row.querySelector<HTMLElement>(
      '[data-original-control="show-shadow"]',
    )!;
    shadowTrigger.classList.toggle("active", shadow.checked);
    shadowTrigger.setAttribute("aria-pressed", String(shadow.checked));
    shadowTrigger.setAttribute(
      "aria-expanded",
      String(this.shadowControls.compact.opened),
    );
    for (const key of ["sensitivity", "softness"] as const) {
      el<HTMLInputElement>("background-removal-" + key).value = String(
        value.background[key],
      );
      el("background-removal-" + key + "-value").textContent =
        value.background[key] + "%";
    }
    el<HTMLInputElement>("background-removal-color").value =
      value.background.color || "#ffffff";
    el("background-removal-auto").classList.toggle(
      "active",
      !value.background.color,
    );
    el("background-removal-pick").classList.toggle("active", this.picking);
    el<HTMLInputElement>("background-removal-enclosed").checked =
      value.background.mode === "all";
    el("background-removal-status").textContent = value.background.enabled
      ? "Вкл"
      : "Выкл";
    el("image-edges-group")
      .querySelectorAll<HTMLElement>("[data-edge-style]")
      .forEach((b) =>
        b.setAttribute(
          "aria-pressed",
          String(b.dataset.edgeStyle === value.edge.style),
        ),
      );
    el("image-edges-group").querySelector<HTMLInputElement>(
      ".image-edge-amount input",
    )!.value = String(value.edge.amount);
    el("image-edges-group").querySelector("output")!.textContent =
      value.edge.amount + "%";
    syncOriginalCompact("native-effects-compact");
  }
}
